import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { errorText } from './api';

/** Bausteine der Verwaltung: Karte, Speichern-Leiste, Feld, Bild-Upload, Bestätigung. */

export function Card({ title, right, children, className, as = 'div', onSubmit }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; as?: 'div' | 'form'; onSubmit?: (e: FormEvent) => void }) {
  const Tag = as;
  return (
    <Tag className={`adm-card${className ? ` ${className}` : ''}`} {...(as === 'form' && onSubmit ? { onSubmit } : {})}>
      {(title || right) && (
        <div className="adm-card__head">
          {title && <h2 className="adm-card__title">{title}</h2>}
          {right}
        </div>
      )}
      {children}
    </Tag>
  );
}

export function Field({ label, hint, children, id }: { label: string; hint?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="faint small">{hint}</span>}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle__track" aria-hidden="true" />
      <span>
        {label}
        {hint && <span className="faint small toggle__hint">{hint}</span>}
      </span>
    </label>
  );
}

/** Speichern-Knopf mit Zustand: bitte warten, gespeichert, Fehler. */
export function useSaver() {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  async function run<T>(action: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const r = await action();
      setSaved(true);
      timer.current = setTimeout(() => setSaved(false), 2500);
      return r;
    } catch (e) {
      setError(errorText(e));
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, saved, error, run, setError };
}

export function SaveBar({ saver, dirty, label = 'Speichern', children }: { saver: ReturnType<typeof useSaver>; dirty: boolean; label?: string; children?: ReactNode }) {
  return (
    <div className="save-bar">
      <button type="submit" className="button" disabled={saver.busy || !dirty}>
        {saver.busy ? 'Bitte warten …' : label}
      </button>
      {children}
      {saver.saved && <span className="muted small">Gespeichert.</span>}
      {saver.error && <span className="error-inline">{saver.error}</span>}
    </div>
  );
}

export function ImageUpload({ current, alt, onUpload, onRemove, hint, previewClass }: { current: string | null; alt: string; onUpload: (file: File) => Promise<void>; onRemove: () => Promise<void>; hint: string; previewClass?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorText(e, 'Das hat nicht geklappt. Bitte versuch es mit einer anderen Datei.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="image-upload">
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void run(() => onUpload(f)); }} />
      {current && <img className={`image-upload__preview${previewClass ? ` ${previewClass}` : ''}`} src={current} alt={alt} />}
      <div className="row-actions">
        <button type="button" className="button button--secondary button--small" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? 'Lädt hoch …' : current ? 'Anderes Bild wählen' : 'Bild hochladen'}
        </button>
        {current && (
          <button type="button" className="link-button" disabled={busy} onClick={() => void run(onRemove)}>
            Entfernen
          </button>
        )}
      </div>
      <span className="faint small">{hint}</span>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export function Tag({ tone, children }: { tone: 'ok' | 'muted' | 'warn' | 'brand'; children: ReactNode }) {
  return <span className={`tag tag--${tone}`}>{children}</span>;
}

export function confirmAction(message: string): boolean {
  return window.confirm(message);
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

export function formatDateOnly(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
}

/** „1:05:30“ ↔ Sekunden – für Sprungmarken und Laufzeiten. */
export function parseClock(text: string): number | null {
  const parts = text.trim().split(':').map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p)) || parts.length === 0 || parts.length > 3) return null;
  const nums = parts.map(Number);
  if (nums.length === 1) return nums[0]!;
  if (nums.length === 2) return nums[0]! * 60 + nums[1]!;
  return nums[0]! * 3600 + nums[1]! * 60 + nums[2]!;
}
